import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useApi, errorMessageFromResponse } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { logger } from '../../../lib/logger';
import {
  FlowViewsResponseSchema,
  SavedViewWireSchema,
  type FlowViewsResponse,
  type SavedViewCreateBody,
  type SavedViewUpdateBody,
  type SavedViewWire,
} from '@shared/flow/view';

/**
 * Saved-views data layer (plan U6) — the react-query half of the switcher.
 *
 * Cached indefinitely and invalidated on every mutation (project rule:
 * cache-indefinitely + invalidate-on-mutation, Decision 13). There is no TTL
 * anywhere in here: a saved view only changes when somebody saves, renames,
 * or deletes one, and all three go through `useFlowViewMutations`, which
 * invalidates the entity's list.
 *
 * Still cache-indefinitely + invalidate-on-mutation (CLAUDE.md Caching Rules):
 * each mutation also writes its response into the cached list before
 * invalidating, which only removes the gap between the response and the refetch.
 *
 * Keyed PER ENTITY (`['flow-views', entity]` — one of `'contact'`, `'account'`,
 * or `'signal'`) because the endpoint is per entity: a view's columns and
 * filter fields belong to one catalog, so the lists are never interchangeable.
 *
 * Presets are NOT in here. They are code-defined (`CONTACT_PRESETS` /
 * `ACCOUNT_PRESETS`) with slug ids and no DB row, so resolving a preset id
 * costs no request at all (Decision 12) — `useViewStateMachine` checks
 * presets first and only consults this list for numeric ids.
 *
 * ── F11: SANCTIONED CACHING EXCEPTION ────────────────────────────────────
 * This query is `staleTime: Infinity` like everything else in the app
 * (project rule: cache-indefinitely + invalidate-on-mutation, no TTLs) — but
 * unlike everything else, its data is SHARED across users (`flow_views` rows
 * are team-visible) and this client has NO invalidation channel for another
 * user's mutation: there's no websocket/SSE push for saved-views the way
 * `artifact:updated` covers artifacts, so a teammate renaming or deleting a
 * shared view would otherwise never reach an already-open tab. Two narrow,
 * explicitly-scoped mitigations, both on THIS query only (every other query
 * keeps pure cache-indefinitely + invalidate-on-own-mutation):
 *   (a) `refetchOnWindowFocus: 'always'` here — the one query in the app
 *       that differs from the default query config. Plain `true` would be
 *       insufficient: with `staleTime: Infinity` this query is by definition
 *       never "stale", and react-query's `true` only refetches STALE data on
 *       focus — `'always'` is the value that bypasses that staleness check.
 *   (b) an explicit `refetchViews()` (exposed on `FlowViewMachine`, called
 *       by `ViewSwitcher`/`BrowseAllModal` when either one OPENS).
 * This does not reintroduce a TTL: between opens/focus events the cache is
 * still indefinite, exactly like every other query.
 */

// Imported (for local use below) AND re-exported, not a second declaration
// (bead terra-signals-tab-ui-signals-tab-pr1-2sx.7): `FlowViewEntity` has
// exactly one source of truth, `@shared/flow/filter-fields.ts:715`, which
// both server and client import. A separately hand-written two-entity
// version here used to drift from the shared entity type the moment a third
// entity ('signal') was added there.
import type { FlowViewEntity } from '@shared/flow/filter-fields';
import { invalidateFavoriteCaches } from '../favorites/useFavoriteMutations';
import { backgroundWrite } from '../../../lib/background-write';
export type { FlowViewEntity } from '@shared/flow/filter-fields';

/** Root of the query key; the entity is appended. Exported so tests and
 *  future units invalidate the same key this module writes. */
export const FLOW_VIEWS_QUERY_KEY = ['flow-views'] as const;

export function flowViewsQueryKey(entity: FlowViewEntity) {
  return [...FLOW_VIEWS_QUERY_KEY, entity] as const;
}

/**
 * Shared options for every `useQuery` observer on `flowViewsQueryKey(entity)`
 * — `useFlowViews`, `useFlowPresetUsage`, and anything future that reads a
 * different slice of the same `GET /api/flow/views` response via `select`.
 * One `queryFn`/cache entry, fetched once per entity regardless of how many
 * selectors observe it (react-query dedupes same-key observers).
 */
function flowViewsQueryOptions(entity: FlowViewEntity, apiFetch: ReturnType<typeof useApi>['apiFetch']) {
  return {
    queryKey: flowViewsQueryKey(entity),
    staleTime: Infinity,
    // F11 (see module doc above) — the one query in the app that refetches
    // on window focus, because it is cross-user shared data with no other
    // invalidation channel. MUST be `'always'`, not `true`: react-query's
    // `refetchOnWindowFocus: true` only refetches STALE data, and with
    // `staleTime: Infinity` this query is by definition never stale — `true`
    // here would silently no-op forever. `'always'` bypasses the staleness
    // check.
    refetchOnWindowFocus: 'always' as const,
    queryFn: async (): Promise<FlowViewsResponse> => {
      return fetchJson(apiFetch, FlowViewsResponseSchema, `/api/flow/views?entity=${entity}`);
    },
  };
}

export interface UseFlowViewsOptions {
  /**
   * Plan 2026-09-28-004 U5/D7 — `SavedViewsPane`'s entity tabs mount TWO
   * extra `useFlowViews` observers (the tab's own OTHER two entities,
   * `OTHER_ENTITIES[machine.entity]`) that must refetch every time the pane
   * OPENS (B9 parity with `BrowseAllModal`'s open-time refetch), the same
   * way `machine.refetchViews()` already forces its OWN entity's list.
   * `staleTime: Infinity` means a bare remount would NOT refetch — this is
   * the one knob a caller can opt into to bypass that. Additive and
   * default-`undefined` (spread only when supplied), so every existing
   * `useFlowViews(entity)` call site is byte-identical.
   */
  refetchOnMount?: 'always';
}

export function useFlowViews(
  entity: FlowViewEntity,
  options?: UseFlowViewsOptions,
): UseQueryResult<SavedViewWire[]> {
  const { apiFetch } = useApi();

  return useQuery({
    ...flowViewsQueryOptions(entity, apiFetch),
    ...(options?.refetchOnMount !== undefined ? { refetchOnMount: options.refetchOnMount } : {}),
    select: data => data.views,
  });
}

/**
 * F12 — usage counts for THIS entity's code-defined preset slugs
 * (`flow_view_usage`, string-keyed, Decision 4). Read from the SAME cached
 * `GET /api/flow/views` response `useFlowViews` reads (same query key, same
 * `queryFn` — react-query serves both from one network fetch), just sliced
 * differently via `select`. A preset with no counter row is absent from the
 * map, never fabricated as `0`.
 */
export function useFlowPresetUsage(entity: FlowViewEntity): UseQueryResult<Record<string, number>> {
  const { apiFetch } = useApi();

  return useQuery({
    ...flowViewsQueryOptions(entity, apiFetch),
    select: data => data.presetUsage ?? {},
  });
}

export interface FlowViewMutations {
  createView: (body: SavedViewCreateBody) => Promise<SavedViewWire>;
  updateView: (id: string, body: SavedViewUpdateBody) => Promise<SavedViewWire>;
  deleteView: (id: string) => Promise<void>;
  /** Usage counter (`POST /api/flow/views/:id/open`). Fire-and-forget by
   *  contract: opening a view must never fail because a counter write did,
   *  so this returns void and swallows errors after logging them. Accepts
   *  preset slugs — `flow_view_usage` is string-keyed (Decision 4). */
  recordViewOpen: (id: string) => void;
  isMutating: boolean;
}

export function useFlowViewMutations(entity: FlowViewEntity): FlowViewMutations {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  // A saved view of any tab can be a favorite's target (its label, its
  // "View removed" state, its count), so every view mutation also refreshes
  // 'flow-favorites' and 'flow-favorite-counts'.
  const invalidate = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: flowViewsQueryKey(entity) }),
        invalidateFavoriteCaches(queryClient),
      ]),
    [queryClient, entity],
  );

  // Write-through: patch the cached list from the mutation response so the UI
  // is right before the invalidation refetch lands. No cache entry -> no write.
  const writeThrough = useCallback(
    (patch: (views: SavedViewWire[]) => SavedViewWire[]) => {
      queryClient.setQueryData<FlowViewsResponse>(flowViewsQueryKey(entity), old =>
        old && Array.isArray(old.views) ? { ...old, views: patch(old.views) } : old,
      );
    },
    [queryClient, entity],
  );

  // Every mutation error is toasted verbatim (`useViewStateMachine`,
  // `SavedViewsPane`, `BrowseAllModal`) — carry the server's own `problem()`
  // message or plain copy, never a raw status code (fix-plan Unit 2).
  const create = useMutation({
    mutationFn: async (body: SavedViewCreateBody): Promise<SavedViewWire> => {
      return fetchJson(apiFetch, SavedViewWireSchema, '/api/flow/views', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    onSuccess: row => {
      writeThrough(views => [...views, row]);
      return invalidate();
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: SavedViewUpdateBody }): Promise<SavedViewWire> => {
      return fetchJson(apiFetch, SavedViewWireSchema, `/api/flow/views/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    onSuccess: row => {
      writeThrough(views => views.map(v => (v.id === row.id ? row : v)));
      return invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await apiFetch(`/api/flow/views/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await errorMessageFromResponse(res, 'Failed to delete view'));
    },
    onSuccess: (_data, id) => {
      writeThrough(views => views.filter(v => v.id !== id));
      return invalidate();
    },
  });

  const createView = useCallback((body: SavedViewCreateBody) => create.mutateAsync(body), [create]);
  const updateView = useCallback(
    (id: string, body: SavedViewUpdateBody) => update.mutateAsync({ id, body }),
    [update],
  );
  const deleteView = useCallback((id: string) => remove.mutateAsync(id), [remove]);

  const recordViewOpen = useCallback((id: string) => {
    void (async () => {
      try {
        await backgroundWrite(apiFetch, `/api/flow/views/${encodeURIComponent(id)}/open`, { method: 'POST' });
      } catch (err) {
        logger.warn({ err, viewId: id }, '[flow-views] usage ping failed');
      }
    })();
  }, [apiFetch]);

  return useMemo(
    () => ({
      createView,
      updateView,
      deleteView,
      recordViewOpen,
      isMutating: create.isPending || update.isPending || remove.isPending,
    }),
    [createView, updateView, deleteView, recordViewOpen, create.isPending, update.isPending, remove.isPending],
  );
}
